import React, { useState, useEffect, useMemo } from "react";
import { useMediaQuery, CircularProgress } from "@mui/material";
import CustomPopup from "../../../Common/CustomPopup";
import { OutlineButton } from "../../../Common/Buttons";
import gfArrowLeftOutlined from "../../../../assets/images/gfArrowLeftOutlined.png";
import checkDate from "../../../Home/checkDate";
import formatTime from "../../../Home/formatTime";
import styles from "./index.module.css";
import CustomDropdown from "../../../Common/CustomDropdown";

const moment = require("moment-timezone");

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * DateTimePickerStep — common date/time picker used by both the Book Trial
 * flow and the Reschedule flow.
 *
 * Props:
 *  open            boolean
 *  onClose         function
 *  onGoBack        function   — navigates back to the previous step
 *  onConfirm       function({ slotId, slotDate, slotTime })
 *  timezoneValue   string     — IANA timezone e.g. "Asia/Calcutta"
 *  timeZones       array      — list of available timezones
 *  onTimezoneChange function(tz)
 *  courseId         string|number
 *  allSlots        object     — slot data grouped by date key, from API
 *  slotsLoading    boolean
 *  onDateSelect    function(dateString) — triggers parent to refetch if needed
 *  mode            "book" | "reschedule"  — controls title + CTA copy. Defaults to "book".
 *  title           string?    — optional explicit override for the title.
 *  ctaLabel        string?    — optional explicit override for the CTA button label.
 *  submitting      boolean    — disables CTA while an async submit is in flight.
 */
const MODE_COPY = {
  book: {
    title: "You're about to book a free trial class",
    ctaLabel: "Confirm and book trial class",
  },
  reschedule: {
    title: "You're about to reschedule your trial class",
    ctaLabel: "Confirm and Reschedule",
  },
};

const DateTimePickerStep = ({
  open,
  onClose,
  onGoBack,
  onConfirm,
  timezoneValue,
  timeZones = [],
  onTimezoneChange,
  courseId,
  allSlots = {},
  slotsLoading = false,
  onDateSelect,
  mode = "book",
  title,
  ctaLabel,
  submitting = false,
}) => {
  const copy = MODE_COPY[mode] || MODE_COPY.book;
  const resolvedTitle = title ?? copy.title;
  const resolvedCtaLabel = ctaLabel ?? copy.ctaLabel;
  const isMobile = useMediaQuery("(max-width: 480px)");

  const [selectedDateIdx, setSelectedDateIdx] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Build next 7 days based on timezone
  const nextSevenDays = useMemo(() => {
    if (!timezoneValue) return [];
    const startDate = new Date(
      new Date().toLocaleString("en-US", { timeZone: timezoneValue }),
    );
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      days.push({
        dayName: DAY_NAMES_SHORT[d.getDay()],
        date: d.getDate(),
        month: MONTH_NAMES_SHORT[d.getMonth()],
        year: d.getFullYear(),
        fullDateStr: `${["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d.getDay()]}, ${d.getDate()} ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getFullYear()}`,
        dateObj: new Date(d),
      });
    }
    return days;
  }, [timezoneValue]);

  // Auto-select first day when days are available
  useEffect(() => {
    if (nextSevenDays.length > 0) setSelectedDateIdx(0);
  }, [nextSevenDays]);

  // Reset selection when timezone or courseId changes
  useEffect(() => {
    setSelectedDateIdx(0);
    setSelectedSlot(null);
  }, [timezoneValue, courseId]);

  // Filter slots for the selected date
  const filteredSlots = useMemo(() => {
    if (!nextSevenDays[selectedDateIdx]) return [];
    const day = nextSevenDays[selectedDateIdx];
    const key = moment(day?.dateObj).format("DD MMM YYYY (ddd)");
    const daySlots = allSlots?.[key] || [];
    return daySlots
      .filter((slot) => checkDate(slot?.slotLocalDateTime, timezoneValue))
      .sort((a, b) => (a?.slotDateTimeUtc < b?.slotDateTimeUtc ? -1 : 1));
  }, [selectedDateIdx, allSlots, nextSevenDays, timezoneValue]);

  const handleDateClick = (idx) => {
    setSelectedDateIdx(idx);
    setSelectedSlot(null);
    onDateSelect?.(nextSevenDays[idx]?.fullDateStr);
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
  };

  const handleConfirm = () => {
    if (!selectedSlot) return;
    onConfirm?.({
      slotId: selectedSlot?.mentorDetails?.[0]?.slotId,
      slotDate: selectedSlot?.slotLocalDateTime,
      slotTime: formatTime(new Date(selectedSlot?.slotLocalDateTime)),
    });
  };

  const isValid = selectedSlot !== null;

  const tzAbbr = timezoneValue ? moment.tz(timezoneValue).format("z") : "";

  return (
    <CustomPopup
      open={open}
      onClose={onClose}
      showCloseButton
      width={isMobile ? 353 : 778}
      padding="0"
      className={isMobile ? styles.popupMobile : styles.popupWeb}
    >
      {/* ── Back button ── */}
      <button
        className={styles.backButton}
        onClick={onGoBack}
        type="button"
        aria-label="Go back"
      >
        <img src={gfArrowLeftOutlined} alt="Back" />
      </button>

      <div className={styles.inner}>
        {/* ── Title ── */}
        <p className={styles.title}>{resolvedTitle}</p>
        <p className={styles.subtitle}>Choose your preferences:</p>

        {/* ── Pick a date ── */}
        <p className={styles.sectionLabel}>Pick a date</p>

        {/* Timezone selector */}
        <CustomDropdown
          compact
          options={timeZones.map((tz) => ({
            label: tz?.timeZones,
            value: tz?.value,
            img: tz?.img,
          }))}
          value={timezoneValue}
          onChange={(e) => onTimezoneChange?.(e.target.value)}
          placeholder="Select timezone"
        />

        {/* Week calendar */}
        <div className={styles.calendarRow}>
          {nextSevenDays.map((day, idx) => (
            <div
              key={idx}
              className={`${styles.dayCard} ${selectedDateIdx === idx ? styles.dayCardSelected : ""}`}
              onClick={() => handleDateClick(idx)}
            >
              <span className={styles.dayName}>{day?.dayName}</span>
              <span className={styles.dayDate}>{day?.date}</span>
              <span className={styles.dayMonth}>{day?.month}</span>
            </div>
          ))}
        </div>

        {/* ── Pick a time ── */}
        <p className={styles.sectionLabel}>Pick a time</p>

        <div className={styles.slotsContainer}>
          {slotsLoading ? (
            <CircularProgress size={28} />
          ) : filteredSlots.length > 0 ? (
            <div className={styles.timeGrid}>
              {filteredSlots.map((slot) => {
                const timeLabel = formatTime(new Date(slot?.slotLocalDateTime));
                const isSelected =
                  selectedSlot?.mentorDetails?.[0]?.slotId ===
                  slot?.mentorDetails?.[0]?.slotId;
                return (
                  <div
                    key={slot?.mentorDetails?.[0]?.slotId}
                    className={`${styles.timeChip} ${isSelected ? styles.timeChipSelected : ""}`}
                    onClick={() => handleSlotClick(slot)}
                  >
                    {timeLabel}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className={styles.noSlots}>
              No slots available for this date. Please choose another date.
            </p>
          )}
        </div>

        {/* ── CTA ── */}
        <div className={styles.ctaWrapper}>
          <OutlineButton
            onClick={handleConfirm}
            className={`${styles.ctaBtn} ${!isValid || submitting ? styles.ctaBtnDisabled : ""}`}
            disabled={!isValid || submitting}
          >
            <span className={styles.ctaLabel}>
              {submitting ? "Please wait..." : resolvedCtaLabel}
            </span>
          </OutlineButton>
        </div>
      </div>
    </CustomPopup>
  );
};

export default DateTimePickerStep;
